/**
 * Port of PlayerController (+ dash, powerup, level-data partials), Squishable and the powerups.
 * Field names follow the C# source so the logic can be compared line by line.
 */
import { num } from '../../model/unityProps.ts';
import type { SpriteRef } from '../../data/gameData.ts';
import { BodyType, MATERIALS, type ContactPoint, type PhysMat, type SimBody, type SimCollider } from './world.ts';
import { GameBehaviour, MATERIAL_DATA, getMaterialData, emptyInput, type ExtMaterialData, type GamePlayer, type PlayerInput } from './game.ts';
import { FIXED_DT, FrameCounter, PrimaryWaysToDie, sign, vangle, vdot, vlen, vnorm, type Deathod, type RGBA, type V2 } from './unity.ts';
import { DeadQuarterExploder } from './mechanics/effects.ts';

export const enum PlayerState {
  Playing,
  Dying,
  Permadead,
  InDoor,
}

export const enum DisplayState {
  Null,
  StandingLeft,
  StandingRight,
  CrouchedLeft,
  CrouchedRight,
  LookingUpLeft,
  LookingUpRight,
}

export const enum ContactState {
  Grounded,
  Sliding,
  Wall,
  None,
}

export const enum PowerupType {
  None,
  BlockDude,
  SwitchPlaces,
  DoubleDash,
  BigDude,
  Jetpack,
}

/** objects a player can enter (Door) */
export interface DoorLike {
  go: import('./world.ts').SimGo;
  onPlayerEntered(p: PlayerController): void;
  onPlayerLeft(p: PlayerController): void;
  doorBounds(): [number, number, number, number];
}

/** BodyControlPanel */
export interface ControlPanelLike {
  startControl(p: PlayerController): void;
  endControl(): void;
}

/** anything with BeginDeath (IRespawnable) */
export interface Respawnable {
  beginDeath(d: Deathod): void;
}

export function isRespawnable(b: unknown): b is Respawnable {
  return !!b && typeof (b as any).beginDeath === 'function';
}

/** call BeginDeath on every IRespawnable of a GameObject */
export function killRespawnables(go: import('./world.ts').SimGo, d: Deathod) {
  for (const b of go.behaviours.slice()) if (isRespawnable(b)) b.beginDeath(d);
}

// ---------- powerups ----------

export abstract class Powerup {
  player: PlayerController;
  spriteOverride: SpriteRef | null = null;
  isActive = false;
  constructor(player: PlayerController) {
    this.player = player;
  }
  abstract type: PowerupType;
  removePowerup() {
    this.resetPlayer();
    this.player.powerup = null;
  }
  powerupPressed() {}
  powerupReleased() {}
  resetPlayer() {}
  fixedUpdate() {}
  updatePlayerColliderInfo(): boolean {
    return false;
  }
  canDie(_d: Deathod): boolean {
    return true;
  }
  cloneState(): this {
    const c = Object.create(Object.getPrototypeOf(this));
    for (const k of Object.keys(this)) {
      const v = (this as any)[k];
      c[k] = v instanceof FrameCounter ? v.copy() : v;
    }
    return c;
  }
}

export class Jetpack extends Powerup {
  type = PowerupType.Jetpack;
  startingThrustFrames = 150;
  thrustRemainingFrames = 150;
  isPlayerGrounded = false;
  isJetpacking = false;
  refill(p = 1) {
    this.thrustRemainingFrames = Math.trunc(this.startingThrustFrames * p);
  }
  override powerupPressed() {
    this.isJetpacking = true;
  }
  override powerupReleased() {
    this.isJetpacking = false;
  }
  override fixedUpdate() {
    const p = this.player;
    if (this.isJetpacking && this.thrustRemainingFrames > 0) {
      if (p.body.vy < p.jetpackMaxVelocity) {
        p.body.addForce({ x: 0, y: p.jetpackThrust });
        this.thrustRemainingFrames--;
        if (this.thrustRemainingFrames === 0) p.sound('jetpackEmpty');
      }
    }
    if (this.isPlayerGrounded) this.thrustRemainingFrames = this.startingThrustFrames;
  }
  get fuel() {
    return this.thrustRemainingFrames / this.startingThrustFrames;
  }
}

export class BigDude extends Powerup {
  type = PowerupType.BigDude;
  originalScale: [number, number];
  originalMass: number;
  originalJumpVelocity: number;
  originalDashVelocity: number;
  originalPostDashVelocity: number;
  originalMaxMovementSpeed: number;
  originalDashCount: number;
  originalDragBlockForce: number;
  originalAllowWallJump: boolean;
  constructor(p: PlayerController, dontModify = false) {
    super(p);
    this.originalScale = [...p.go.bodyScaleXY] as [number, number];
    this.originalMass = p.body.mass;
    this.originalJumpVelocity = p.jumpVelocity;
    this.originalDashVelocity = p.dashVelocity;
    this.originalPostDashVelocity = p.postDashVelocity;
    this.originalMaxMovementSpeed = p.maxMovementSpeed;
    this.originalDashCount = p.dashCount;
    this.originalDragBlockForce = p.dragBlockForce;
    this.originalAllowWallJump = p.allowWallJump;
    if (!dontModify) {
      p.go.setBodyScale(4, 4);
      p.body.mass = 8;
      p.body.updateMass();
      p.jumpVelocity = 14.6;
      p.dashVelocity = 50;
      p.postDashVelocity = 12;
      p.maxMovementSpeed = 9;
      p.dragBlockForce = 2000;
      p.allowWallJump = false;
      p.sound('bigDude');
    }
  }
  override updatePlayerColliderInfo() {
    const size = { x: 0.715, y: 0.9675 };
    const off = { x: 0, y: 0 };
    if (this.player.isCrouching) {
      size.y = 0.467;
      off.y -= 0.25;
    }
    this.player.setCollider(size, off, this.player.colliderEdgeRadius);
    return true;
  }
  override resetPlayer() {
    const p = this.player;
    p.go.setBodyScale(this.originalScale[0], this.originalScale[1]);
    p.body.mass = this.originalMass;
    p.body.updateMass();
    p.jumpVelocity = this.originalJumpVelocity;
    p.dashVelocity = this.originalDashVelocity;
    p.postDashVelocity = this.originalPostDashVelocity;
    p.maxMovementSpeed = this.originalMaxMovementSpeed;
    p.dashCount = this.originalDashCount;
    p.dragBlockForce = this.originalDragBlockForce;
    p.allowWallJump = this.originalAllowWallJump;
  }
  override canDie(d: Deathod) {
    return !(d.primary === PrimaryWaysToDie.Spikes || d.primary === PrimaryWaysToDie.Projectile);
  }
}

export class BlockDude extends Powerup {
  type = PowerupType.BlockDude;
  useCounter = FrameCounter.seconds(4);
  cooldownCounter = FrameCounter.seconds(2);
  originalEdgeRadius: number;
  originalMass: number;
  originalLinearDamping: number;
  originalGravityScale: number;
  constructor(p: PlayerController) {
    super(p);
    this.cooldownCounter.markFinished();
    this.originalEdgeRadius = p.colliderEdgeRadius;
    this.originalMass = p.body.mass;
    this.originalLinearDamping = p.body.linearDamping;
    this.originalGravityScale = p.body.gravityScale;
  }
  override powerupPressed() {
    const p = this.player;
    if (p.playerState === PlayerState.Playing && this.cooldownCounter.isFinished()) {
      this.isActive = true;
      p.body.velocity = { x: 0, y: 0 };
      p.isExternallyControlled = true;
      p.colliderEdgeRadius = 0.1;
      p.setCollider({ x: 0.8, y: 0.8 }, { x: 0, y: 0 }, 0.1);
      p.body.mass = 1000;
      p.body.updateMass();
      p.body.linearDamping = 1000;
      p.body.gravityScale = 0;
      this.useCounter.restart();
      p.sound('blockDude');
    }
  }
  override powerupReleased() {
    if (this.player.playerState === PlayerState.Playing && this.isActive) this.resetPlayer();
  }
  override resetPlayer() {
    const p = this.player;
    this.isActive = false;
    p.colliderEdgeRadius = this.originalEdgeRadius;
    p.isExternallyControlled = false;
    p.body.mass = this.originalMass;
    p.body.updateMass();
    p.body.linearDamping = this.originalLinearDamping;
    p.body.gravityScale = this.originalGravityScale;
    this.cooldownCounter.percentage = 1 - this.useCounter.percentage;
    if (this.cooldownCounter.isNotRunning()) this.cooldownCounter.start();
    this.useCounter.reset();
  }
  override canDie(d: Deathod) {
    return !(this.isActive && (d.primary === PrimaryWaysToDie.Spikes || d.primary === PrimaryWaysToDie.Projectile || d.primary === PrimaryWaysToDie.Laser));
  }
  override updatePlayerColliderInfo() {
    return this.isActive;
  }
  override fixedUpdate() {
    const p = this.player;
    if (this.isActive) {
      this.spriteOverride = p.characterDirection === 1 ? p.spriteMap.blockDudeRight : p.spriteMap.blockDudeLeft;
      this.useCounter.count();
      if (this.useCounter.isFinished()) p.performPowerupAction('released');
    } else {
      this.spriteOverride = null;
      this.cooldownCounter.count();
    }
  }
}

export class DoubleDash extends Powerup {
  type = PowerupType.DoubleDash;
  originalDashCount: number;
  constructor(p: PlayerController, dontModify = false) {
    super(p);
    this.originalDashCount = p.dashCount;
    if (!dontModify) {
      p.dashCount = 2;
      p.sound('doubleDash');
    }
  }
  override resetPlayer() {
    this.player.dashCount = this.originalDashCount;
  }
}

export function constructPowerup(t: PowerupType, p: PlayerController): Powerup | null {
  switch (t) {
    case PowerupType.BlockDude: return new BlockDude(p);
    case PowerupType.DoubleDash: return new DoubleDash(p);
    case PowerupType.BigDude: return new BigDude(p);
    case PowerupType.Jetpack: return new Jetpack(p);
  }
  return null;
}

// ---------- sprite map ----------

export interface SpriteMap {
  standingLeft: SpriteRef | null;
  standingRight: SpriteRef | null;
  lookingUpLeft: SpriteRef | null;
  lookingUpRight: SpriteRef | null;
  crouchedLeft: SpriteRef | null;
  crouchedRight: SpriteRef | null;
  blockDudeLeft: SpriteRef | null;
  blockDudeRight: SpriteRef | null;
}

const UP_RIGHT = vnorm({ x: 1, y: 1 }), UP_LEFT = vnorm({ x: -1, y: 1 }), DOWN_LEFT = vnorm({ x: -1, y: -1 }), DOWN_RIGHT = vnorm({ x: 1, y: -1 });

function roundToNearest45(v: V2): V2 {
  const angle = (Math.atan2(v.y, v.x) * 180) / Math.PI;
  const m = vlen(v);
  const s = (d: V2) => ({ x: d.x * m, y: d.y * m });
  if (angle >= -22.5 && angle < 22.5) return s({ x: 1, y: 0 });
  if (angle >= 22.5 && angle < 67.5) return s(UP_RIGHT);
  if (angle >= 67.5 && angle < 112.5) return s({ x: 0, y: 1 });
  if (angle >= 112.5 && angle < 157.5) return s(UP_LEFT);
  if (angle >= 157.5 || angle < -157.5) return s({ x: -1, y: 0 });
  if (angle >= -157.5 && angle < -112.5) return s(DOWN_LEFT);
  if (angle >= -112.5 && angle < -67.5) return s({ x: 0, y: -1 });
  if (angle >= -67.5 && angle < -22.5) return s(DOWN_RIGHT);
  return { x: 0, y: 0 };
}

const STEP_LENGTH = 1.6;

/** bits of PlayerController.soundLoops */
export const SOUND_LOOP = { jetpack: 1, wallSlide: 2, pushBlock: 4 } as const;

const bool = (v: any, d = false) => (v === undefined ? d : String(v) !== '0');

// ---------- PlayerController ----------

export class PlayerController extends GameBehaviour implements GamePlayer {
  static override order = 5;
  override noSave = ['input', 'index', 'color', 'totalDeaths', 'trail', 'spriteMap', 'body', 'playerCollider', 'groundedMaterial', 'inAirMaterial', 'savedSortLayer', 'invulnerableToDeath', 'livesLabel'];

  // meta
  index = 0;
  color: RGBA = [1, 1, 1, 1];
  input: PlayerInput = emptyInput();
  totalDeaths = 0;
  initialSpawn: V2 | null = null;
  /** recent dash trail positions (cosmetic) */
  trail: { x: number; y: number; t: number; sprite: SpriteRef | null; scale: number }[] = [];

  // serialized settings
  allowJump = true;
  allowDash = true;
  allowWallJump = true;
  allowDashLaunch = false;
  limitDashLaunchMultiplier = 0;
  allowControl = true;
  killOnPressRestart = true;
  jumpVelocity = 7;
  jetpackThrust = 150;
  jetpackMaxVelocity = 4;
  wallJumpXVelocity = 10;
  wallJumpYVelocity = 4;
  dashVelocity = 7;
  postDashVelocity = 2;
  allowWallSlide = true;
  wallSlideVelocity = -2.5;
  onlyWallSlideIfHoldingTowardsWall = true;
  maxFallSpeed = -30;
  dashFrames = 30;
  dashCount = 1;
  allowDashAgainFrames = 10;
  onlyAllow45DegreeDash = false;
  jumpHoldFrames = 20;
  jumpReleaseMultiplier = 0.3;
  maxMovementSpeed = 7;
  dragBlockForce = 100;
  movePushingDownBlockForce = 50;
  dragBlockMaxSpeed = 3;
  grabDistance = 0.25;
  pushAcceleration = 5;
  maxGroundAngle = 47;
  maxWallAngle = 1;
  maxPushAngle = 45;
  maxCoyoteCount = 7;
  jumpDisableCounterMax = 4;
  wallSlideCounterMax = 100000;
  postWallJumpCounterMax = 25;

  // state
  isGrounded = false;
  isPushedDown = false;
  pushedDownImpulse = 0;
  bodyPushingDown: SimBody | null = null;
  isPushedDownSlope = false;
  groundedOnBodySpeed: V2 = { x: 0, y: 0 };
  isOnFlatGround = false;
  groundNormal: V2 = { x: 0, y: 0 };
  groundNormalAngle = 90;
  isGroundedCoyote = false;
  isCollidingLeftWall = false;
  isCollidingLeftWithPlayer = false;
  isCollidingRightWall = false;
  isCollidingRightWithPlayer = false;
  isPushingLeft = false;
  isPushingRight = false;
  isDragging = false;
  playerMovementSpeed = 0;
  coyoteCounter = 0;
  wallJumpCoyoteCounter = new FrameCounter(7);
  wallJumpDir = 0;
  jumpDisableCounter = 0;
  jumpHoldCounter = 0;
  wallSlideCounter = 0;
  postWallJumpCounter = 0;
  dashCounter = 0;
  dashAgainCounter = 0;
  dashFinishedThisFrame = false;
  hasDashBeenLimited = false;
  dashDirection: V2 = { x: 0, y: 0 };
  dashCountSinceLastGround = 0;
  jumpDown = false;
  jumpReleasedThisFrame = false;
  /** kinematic flight while Ctrl / LB is held and the control stick moves */
  isUsingDebugControl = false;
  /** sorting layer put back when debug flight ends */
  private savedSortLayer: string | null = null;
  private static readonly FLY_LAYER = 'This is your brain on drugs';
  private savedFlyPos: V2 | null = null;
  hasJumpedSinceLastPress = false;
  dashPressedThisFrame = false;
  groundMaterialData: ExtMaterialData = MATERIAL_DATA.air;
  wallMaterialData: ExtMaterialData = MATERIAL_DATA.air;
  lastWallMaterialData: ExtMaterialData | null = null;
  colliderEdgeRadius = 0.05;
  colliderOffset = 0;
  movementVector: V2 = { x: 0, y: 0 };
  movementVector45: V2 = { x: 0, y: 0 };
  playerVelocityDir = 0;
  movementVectorDir = 0;
  characterDirection = 1;
  isCrouching = false;
  isPressingCrouch = false;
  isWalkingOnSlope = false;
  previousDraggableRigidbody: SimBody | null = null;
  draggableRigidbody: SimBody | null = null;
  characterDisplayState = DisplayState.StandingRight;
  characterContactState = ContactState.None;
  groundSlideDirection = 0;
  controlPanelInRange: ControlPanelLike | null = null;
  doorInRange: DoorLike | null = null;
  isUsingControlPanel = false;
  playerState = PlayerState.Playing;
  /** Debug pause: "I dont wanna die". */
  invulnerableToDeath = false;
  /** King of the hill life count, drawn next to the player. */
  livesLabel: string | null = null;
  isExternallyControlled = false;
  lastPlayerVelocity: V2 = { x: 0, y: 0 };
  powerup: Powerup | null = null;
  playerSizeBeforeCrouch: V2 = { x: 0.65, y: 0.86 };
  playerOffsetBeforeCrouch: V2 = { x: 0, y: -0.02 };
  playerSizeStanding: V2 = { x: 0.75, y: 1 };
  playerSizeCrouching: V2 = { x: 0.75, y: 0.5 };

  // level data
  coinCount = 0;
  shardCount = 0;
  gemCount = 0;
  deathCount = 0;
  score = 0;
  levelFinishedOnFrame = 0;

  // squishable
  squishCounter = new FrameCounter(4);

  // sound bookkeeping
  /** SOUND_LOOP bits playing for this player. Guests copy them from the host. */
  soundLoops = 0;
  private wasGroundedForSound = true;
  private fallSpeedForSound = 0;
  private stepDistance = 0;
  private dashSpentForSound = false;
  penetrationThreshold = 0.1;

  body!: SimBody;
  playerCollider!: SimCollider;
  groundedMaterial: PhysMat | null = MATERIALS.a13fb2305aceebf4980c13a4b30e760e;
  inAirMaterial: PhysMat | null = MATERIAL_DATA.air.material;
  spriteMap!: SpriteMap;

  override awake() {
    const d = this.data;
    const f = (k: string, def: number) => num(d[k], def);
    const b = (k: string, def: boolean) => bool(d[k], def);
    this.allowJump = b('allowJump', true);
    this.allowDash = b('allowDash', true);
    this.allowWallJump = b('allowWallJump', true);
    this.allowDashLaunch = b('allowDashLaunch', false);
    this.limitDashLaunchMultiplier = f('limitDashLaunchMultiplier', 0);
    this.allowControl = b('allowControl', true);
    this.killOnPressRestart = b('killOnPressRestart', true);
    this.jumpVelocity = f('jumpVelocity', 7);
    this.jetpackThrust = f('jetpackThrust', 150);
    this.jetpackMaxVelocity = f('jetpackMaxVelocity', 4);
    this.wallJumpXVelocity = f('wallJumpXVelocity', 10);
    this.wallJumpYVelocity = f('wallJumpYVelocity', 4);
    this.dashVelocity = f('dashVelocity', 7);
    this.postDashVelocity = f('postDashVelocity', 2);
    this.allowWallSlide = b('allowWallSlide', true);
    this.wallSlideVelocity = f('wallSlideVelocity', -2.5);
    this.onlyWallSlideIfHoldingTowardsWall = b('onlyWallSlideIfHoldingTowardsWall', true);
    this.maxFallSpeed = f('maxFallSpeed', -30);
    this.dashFrames = f('dashFrames', 30);
    this.dashCount = f('dashCount', 1);
    this.allowDashAgainFrames = f('allowDashAgainFrames', 10);
    this.onlyAllow45DegreeDash = b('onlyAllow45DegreeDash', false);
    this.jumpHoldFrames = f('jumpHoldFrames', 20);
    this.jumpReleaseMultiplier = f('jumpReleaseMultiplier', 0.3);
    this.maxMovementSpeed = f('maxMovementSpeed', 7);
    this.dragBlockForce = f('dragBlockForce', 100);
    this.movePushingDownBlockForce = f('movePushingDownBlockForce', 50);
    this.dragBlockMaxSpeed = f('dragBlockMaxSpeed', 3);
    this.grabDistance = f('grabDistance', 0.25);
    this.pushAcceleration = f('pushAcceleration', 5);
    this.maxGroundAngle = f('maxGroundAngle', 47);
    this.maxWallAngle = f('maxWallAngle', 1);
    this.maxPushAngle = f('maxPushAngle', 45);
    this.maxCoyoteCount = f('maxCoyoteCount', 7);
    this.jumpDisableCounterMax = f('jumpDisableCounterMax', 4);
    this.wallSlideCounterMax = f('wallSlideCounterMax', 100000);
    this.postWallJumpCounterMax = f('postWallJumpCounterMax', 25);
    this.wallJumpCoyoteCounter = FrameCounter.from(d.wallJumpCoyoteCounter, 7 / 120);
    if (d.groundedMaterial?.guid && MATERIALS[d.groundedMaterial.guid]) this.groundedMaterial = MATERIALS[d.groundedMaterial.guid];

    this.body = this.go.body!;
    this.playerCollider = this.go.colliders.find((c) => c.type === 'BoxCollider2D')!;
    const sh = this.playerCollider.shape;
    if (sh.kind === 'box') {
      this.colliderEdgeRadius = sh.edge;
      this.colliderOffset = sh.oy;
    }
    this.wallJumpCoyoteCounter.markFinished();
    const sm = this.go.comps.get('PlayerControllerSpriteHandler')?.spriteMaps?.[0] ?? {};
    const sp = (r: any): SpriteRef | null => (r?.guid ? this.game.gd.spriteById.get(`${r.guid}:${r.fileID}`) ?? null : null);
    this.spriteMap = {
      standingLeft: sp(sm.standingLeftSprite),
      standingRight: sp(sm.standingRightSprite),
      lookingUpLeft: sp(sm.lookingUpLeftSprite),
      lookingUpRight: sp(sm.lookingUpRightSprite),
      crouchedLeft: sp(sm.crouchedLeftSprite),
      crouchedRight: sp(sm.crouchedRightSprite),
      blockDudeLeft: sp(sm.blockDudeLeftSprite),
      blockDudeRight: sp(sm.blockDudeRightSprite),
    };
    const ex = this.go.comps.get('SpriteExcludeTintPropertyBlockController');
    if (ex && this.go.sprite) {
      const c = ex.excludeColor0;
      if (c) this.go.sprite.excludeTint = [num(c.r), num(c.g), num(c.b), 1];
    }
  }

  override start() {
    this.updateSprite();
  }

  // ---------- helpers ----------

  get position(): V2 {
    return this.body.position;
  }

  setCollider(size: V2, offset: V2, edge: number) {
    const s = this.playerCollider.shape;
    if (s.kind === 'box' && Math.abs(s.w - size.x) < 1e-6 && Math.abs(s.h - size.y) < 1e-6 && Math.abs(s.ox - offset.x) < 1e-6 && Math.abs(s.oy - offset.y) < 1e-6 && Math.abs(s.edge - edge) < 1e-6) return;
    this.playerCollider.setShape({ kind: 'box', w: size.x, h: size.y, ox: offset.x, oy: offset.y, edge });
  }

  colliderSize(): V2 {
    const s = this.playerCollider.shape;
    return s.kind === 'box' ? { x: s.w, y: s.h } : { x: 0.65, y: 0.86 };
  }
  colliderOffsetV(): V2 {
    const s = this.playerCollider.shape;
    return s.kind === 'box' ? { x: s.ox, y: s.oy } : { x: 0, y: 0 };
  }

  isDashing() {
    return this.dashCounter > 0;
  }

  isKindaDyingAlready() {
    return this.playerState === PlayerState.Dying || this.playerState === PlayerState.Permadead || this.playerState === PlayerState.InDoor;
  }

  // ---------- FixedUpdate ----------

  override fixedUpdate() {
    if (this.game.isInEndgame) return;
    const input = this.input;
    this.dashFinishedThisFrame = false;

    if (input.restartPressed && this.killOnPressRestart) this.game.handleLastCheckpoint();

    this.updateInputVariables();

    const wasCollidingLeft = this.isCollidingLeftWall;
    const wasCollidingRight = this.isCollidingRightWall;

    this.movementVector = { ...input.movement };
    this.movementVector45 = roundToNearest45(this.movementVector);
    this.playerVelocityDir = sign(this.body.vx);
    this.movementVectorDir = sign(this.movementVector.x);

    this.enumerateContacts();

    if (this.powerup instanceof Jetpack) this.powerup.isPlayerGrounded = this.isGrounded;

    if (!this.isGrounded) this.coyoteCounter++;
    if (this.coyoteCounter > this.maxCoyoteCount) this.isGroundedCoyote = false;

    this.wallJumpCoyoteCounter.count();

    this.jumpDisableCounter++;
    if (this.postWallJumpCounter > 0) this.postWallJumpCounter++;
    if (this.postWallJumpCounter > this.postWallJumpCounterMax) this.postWallJumpCounter = 0;
    if (!this.isDragging && (!input.interactDown || !this.draggableRigidbody)) {
      if (this.movementVector.x > 0) this.characterDirection = 1;
      if (this.movementVector.x < 0) this.characterDirection = -1;
    }

    this.isPressingCrouch = false;
    this.characterDisplayState = DisplayState.Null;
    if (this.movementVector45.y > 0) {
      this.characterDisplayState = this.characterDirection === 1 ? DisplayState.LookingUpRight : DisplayState.LookingUpLeft;
    } else if (this.movementVector45.y < 0) {
      this.isPressingCrouch = true;
    }

    if (this.isPressingCrouch && !this.isCrouching) {
      this.playerSizeBeforeCrouch = this.colliderSize();
      this.playerOffsetBeforeCrouch = this.colliderOffsetV();
      this.isCrouching = true;
    }
    if (!this.isPressingCrouch && this.isCrouching) this.tryUncrouch();

    if (this.isCrouching) {
      this.characterDisplayState = this.characterDirection === 1 ? DisplayState.CrouchedRight : DisplayState.CrouchedLeft;
    } else if (this.characterDisplayState === DisplayState.Null) {
      this.characterDisplayState = this.characterDirection === 1 ? DisplayState.StandingRight : DisplayState.StandingLeft;
    }

    if (input.powerupPressed) this.performPowerupAction('pressed');
    if (input.powerupReleased) this.performPowerupAction('released');

    this.handleDebugInput();
    this.handleDebugMove();

    if (!this.isExternallyControlled && !this.isUsingDebugControl) {
      this.isDragging = this.isGrounded && !!this.draggableRigidbody && input.interactDown && this.movementVectorDir !== this.characterDirection;
      if (this.isDragging && this.draggableRigidbody) {
        const d = this.draggableRigidbody;
        if (Math.abs(d.vx) > this.dragBlockMaxSpeed) {
          d.vx = Math.sign(d.vx) * this.dragBlockMaxSpeed;
          this.body.vx = d.vx - 0.05;
        } else {
          d.addForce({ x: this.dragBlockForce * this.movementVectorDir, y: 0 });
        }
      }
      if (!this.isDragging && !this.isUsingControlPanel && !this.isUsingDebugControl) {
        this.handleMovement(wasCollidingLeft, wasCollidingRight);
        this.handleJumping();
      }
      if (!this.isUsingControlPanel && !this.isUsingDebugControl) this.handleDashing();
      this.handleInteractables();
    }

    this.powerup?.fixedUpdate();

    this.jumpReleasedThisFrame = false;
    this.dashPressedThisFrame = false;

    this.updateSprite();
    this.lastPlayerVelocity = this.body.velocity;

    this.squishableFixedUpdate();
    this.soundFixedUpdate();
  }

  /** landing, footsteps and dash recharge sounds */
  private soundFixedUpdate() {
    if (this.playerState !== PlayerState.Playing) {
      this.wasGroundedForSound = true;
      this.soundLoops = 0;
      return;
    }
    const grounded = this.isGrounded;
    const drag = this.draggableRigidbody;
    const touchingWall = this.isCollidingLeftWall || this.isCollidingRightWall;
    this.soundLoops =
      (this.powerup instanceof Jetpack && this.powerup.isJetpacking ? SOUND_LOOP.jetpack : 0) |
      (!grounded && touchingWall && this.wallSlideCounter > 0 && this.body.vy < -0.5 ? SOUND_LOOP.wallSlide : 0) |
      (grounded && drag && this.movementVectorDir !== 0 && Math.abs(drag.vx) > 0.3 ? SOUND_LOOP.pushBlock : 0);
    if (grounded && !this.wasGroundedForSound && this.fallSpeedForSound < -4) this.sound('land');
    if (this.dashCountSinceLastGround >= this.dashCount && this.dashCount > 0) this.dashSpentForSound = true;
    else if (this.dashSpentForSound && this.dashCountSinceLastGround === 0) {
      this.dashSpentForSound = false;
      if (grounded) this.sound('dashReady');
    }
    const speed = Math.abs(this.body.vx - this.groundedOnBodySpeed.x);
    if (grounded && this.dashCounter === 0 && speed > 1.5 && !this.isDragging) {
      this.stepDistance += speed * FIXED_DT;
      if (this.stepDistance >= STEP_LENGTH) {
        this.stepDistance -= STEP_LENGTH;
        this.sound('footstep');
      }
    } else this.stepDistance = STEP_LENGTH * 0.7;
    this.wasGroundedForSound = grounded;
    this.fallSpeedForSound = this.body.vy;
  }

  /**
   * Ctrl/LB + IJKL or the right stick flies.
   * While that is held: d-pad left saves a spot, right warps back, up/down change lightness,
   * and down also warps onto the other player.
   * Left trigger + d-pad down saves a checkpoint; left trigger + d-pad left restores it.
   * Otherwise d-pad left/right changes hue.
   */
  private handleDebugInput() {
    const i = this.input;
    if (i.interactDown) return;
    if (i.controlEnabled) {
      if (i.dLeft) this.savedFlyPos = { ...this.body.position };
      if (i.dRight && this.savedFlyPos) this.warpTo(this.savedFlyPos);
      if (i.dUp) this.changeLightness(1);
      if (i.dDown) {
        this.changeLightness(-1);
        this.warpToOther();
      }
    }
    if (i.leftTrigger > 0) {
      if (i.dDown) this.game.saveState();
      if (i.dLeft) this.game.restoreState(false);
      return;
    }
    if (i.controlEnabled) return;
    if (i.dLeft) this.changeHue(-1);
    if (i.dRight) this.changeHue(1);
  }

  private warpTo(pos: V2) {
    this.body.position = { x: pos.x, y: pos.y };
    this.body.velocity = { x: 0, y: 0 };
  }

  /** Control + d-pad down: stand on the camera's other player, or the first other player. */
  private warpToOther() {
    const cam = this.game.camera as { focusedPlayer?: PlayerController } | null;
    const focused = cam?.focusedPlayer;
    const other = focused && focused !== this ? focused : this.game.players.find((p) => p !== this);
    if (!other) return;
    const q = other.go.position;
    this.warpTo({ x: q.x, y: q.y + 1 });
  }

  private handleDebugMove() {
    if (!this.allowControl) return;
    const i = this.input;
    if (i.dashPressed) {
      if (this.isUsingDebugControl) this.setDebugControl(false);
      return;
    }
    const flying = i.controlEnabled && (i.control.x !== 0 || i.control.y !== 0);
    if (flying) {
      if (!this.isUsingDebugControl) this.setDebugControl(true);
      const speed = 0.2 + 0.8 * i.rightTrigger;
      const p = this.body.position;
      this.body.position = { x: p.x + i.control.x * speed, y: p.y + i.control.y * speed };
      this.body.velocity = { x: 0, y: 0 };
      return;
    }
    if (this.isUsingDebugControl && !i.controlEnabled) this.setDebugControl(false);
  }

  private setDebugControl(on: boolean) {
    this.isUsingDebugControl = on;
    const sr = this.go.sprite;
    if (on) {
      this.body.type = BodyType.Kinematic;
      this.body.velocity = { x: 0, y: 0 };
      if (sr && sr.sortingLayer !== PlayerController.FLY_LAYER) {
        this.savedSortLayer = sr.sortingLayer;
        sr.sortingLayer = PlayerController.FLY_LAYER;
      }
    } else {
      this.body.type = BodyType.Dynamic;
      if (sr && sr.sortingLayer === PlayerController.FLY_LAYER) sr.sortingLayer = this.savedSortLayer ?? 'Player';
      this.savedSortLayer = null;
    }
  }

  changeHue(dir: number) {
    const hsl = rgbToHsl(this.color[0], this.color[1], this.color[2]);
    let h = hsl.h + dir * 10;
    if (h > 360) h -= 360;
    if (h < 0) h += 360;
    this.setColor(hslToRgb(h, 1, hsl.l));
  }

  changeLightness(dir: number) {
    const hsl = rgbToHsl(this.color[0], this.color[1], this.color[2]);
    this.setColor(hslToRgb(hsl.h, 1, Math.min(1, Math.max(0, hsl.l + dir * 0.05))));
  }

  setColor(color: RGBA) {
    this.color = [color[0], color[1], color[2], 1];
    if (this.go.sprite) this.go.sprite.color = [...this.color] as RGBA;
  }

  private updateInputVariables() {
    const i = this.input;
    if (i.jumpPressed) {
      this.jumpDown = true;
      this.hasJumpedSinceLastPress = false;
    }
    if (i.jumpReleased) {
      this.jumpDown = false;
      this.jumpReleasedThisFrame = true;
    }
    if (i.dashPressed) this.dashPressedThisFrame = true;
  }

  tryUncrouch() {
    const subFromBottom = 0.2;
    const [sx, sy] = this.go.bodyScaleXY;
    const paddingX = 0.1 / sx, paddingY = 0.1 / sy;
    const size = { x: (this.playerSizeBeforeCrouch.x - paddingX) * sx, y: (this.playerSizeBeforeCrouch.y - (paddingY + subFromBottom)) * sy };
    const off = { x: 0, y: (this.playerOffsetBeforeCrouch.y + subFromBottom / 2) * sy };
    const p = this.body.position;
    const hits = this.world.overlapBox({ x: p.x + off.x, y: p.y + off.y }, size, 0);
    let hasHit = false;
    for (const h of hits) if (h.attachedRigidbody !== this.body && !h.isTrigger) hasHit = true;
    if (!hasHit) this.isCrouching = false;
  }

  updatePlayerColliderInfo() {
    const size = { ...this.playerSizeStanding };
    const offset = { x: 0, y: this.colliderOffset };
    if (this.isCrouching) {
      size.x = this.playerSizeCrouching.x;
      size.y = this.playerSizeCrouching.y;
      offset.y -= (1 - size.y) / 2;
    }
    size.x -= this.colliderEdgeRadius * 2;
    size.y -= (this.colliderEdgeRadius - this.colliderOffset) * 2;
    this.setCollider(size, offset, this.colliderEdgeRadius);
  }

  updateSprite() {
    const sr = this.go.sprite;
    if (!sr) return;
    let s = sr.sprite;
    if (this.powerup?.spriteOverride) s = this.powerup.spriteOverride;
    else {
      const m = this.spriteMap;
      switch (this.characterDisplayState) {
        case DisplayState.StandingLeft: s = m.standingLeft; break;
        case DisplayState.StandingRight: s = m.standingRight; break;
        case DisplayState.CrouchedLeft: s = m.crouchedLeft; break;
        case DisplayState.CrouchedRight: s = m.crouchedRight; break;
        case DisplayState.LookingUpLeft: s = m.lookingUpLeft; break;
        case DisplayState.LookingUpRight: s = m.lookingUpRight; break;
      }
    }
    if (s) sr.sprite = s;
    if (!(this.powerup?.updatePlayerColliderInfo() ?? false)) this.updatePlayerColliderInfo();
  }

  private updateSharedMaterial() {
    this.playerCollider.material =
      !this.isGrounded || ((this.isCollidingLeftWall || this.isCollidingRightWall) && this.playerMovementSpeed === 0 && !this.bodyPushingDown) || this.isPushedDownSlope
        ? this.inAirMaterial
        : this.groundedMaterial;
  }

  private handleMovement(wasCollidingLeft: boolean, wasCollidingRight: boolean) {
    const body = this.body;
    const v = body.velocity;
    const initialVelocity = { x: v.x - this.groundedOnBodySpeed.x, y: v.y - this.groundedOnBodySpeed.y };
    this.isWalkingOnSlope = this.isGrounded && !this.isOnFlatGround && this.playerMovementSpeed !== 0;
    const slopeDirection = { x: this.groundNormal.y, y: -this.groundNormal.x };

    if (this.isWalkingOnSlope) this.playerMovementSpeed = vdot(initialVelocity, slopeDirection);
    else this.playerMovementSpeed = initialVelocity.x;

    if (Math.abs(this.playerMovementSpeed) < 0.001) this.playerMovementSpeed = 0;

    this.updateSharedMaterial();
    if (this.movementVector45.x === 0) {
      if (this.playerMovementSpeed !== 0) this.playerMovementSpeed -= this.groundMaterialData.characterDeceleration * this.playerVelocityDir;
      if (this.playerVelocityDir !== sign(this.playerMovementSpeed)) this.playerMovementSpeed = 0;
    } else {
      const md = this.groundMaterialData;
      let acc = this.playerVelocityDir === this.movementVectorDir || this.playerVelocityDir === 0 ? md.characterAcceleration : md.characterAccelerationAgainst;
      if ((this.isPushingLeft && !this.isCollidingLeftWithPlayer && this.movementVectorDir === -1) || (this.isPushingRight && !this.isCollidingRightWithPlayer && this.movementVectorDir === 1) || (this.isGrounded && this.isPushedDown)) acc = this.pushAcceleration;
      if (!this.isGrounded && this.playerVelocityDir !== this.movementVectorDir && this.postWallJumpCounter > 0) acc = 0;
      const maxSpeed = Math.max(Math.abs(initialVelocity.x), this.maxMovementSpeed);
      let applyInput = false;
      const desired = Math.abs(this.maxMovementSpeed * this.movementVector.x);
      if (Math.abs(initialVelocity.x) < desired) applyInput = true;
      else if (this.movementVectorDir !== this.playerVelocityDir) applyInput = true;
      if (this.characterContactState === ContactState.Sliding && this.movementVectorDir !== this.groundSlideDirection) applyInput = false;
      if (applyInput) {
        this.playerMovementSpeed = Math.max(-maxSpeed, Math.min(maxSpeed, this.playerMovementSpeed + acc * this.movementVectorDir));
        if (this.isWalkingOnSlope && Math.abs(this.playerMovementSpeed) < 1) this.playerMovementSpeed = this.movementVectorDir * 1;
      }
    }

    let fallingSpeed = initialVelocity.y;
    if (fallingSpeed < this.maxFallSpeed && !this.isDashing() && !this.isPushedDown) fallingSpeed = this.maxFallSpeed;

    if (this.wallSlideCounter > this.wallSlideCounterMax) this.wallSlideCounter = 0;
    if (this.wallSlideCounter > 0) this.wallSlideCounter++;
    if ((!wasCollidingLeft && this.isCollidingLeftWall && !this.isCollidingLeftWithPlayer) || (!wasCollidingRight && this.isCollidingRightWall && !this.isCollidingRightWithPlayer)) this.wallSlideCounter++;

    if (this.isCollidingLeftWall || this.isCollidingRightWall) {
      this.wallJumpCoyoteCounter.restart();
      this.wallJumpDir = this.isCollidingLeftWall ? 1 : -1;
    }
    if (!this.isCollidingLeftWall && !this.isCollidingRightWall) this.wallSlideCounter = 0;

    const canWallSlideInput = !this.onlyWallSlideIfHoldingTowardsWall || (this.movementVectorDir === -1 && this.isCollidingLeftWall) || (this.movementVectorDir === 1 && this.isCollidingRightWall);
    if (canWallSlideInput && this.allowWallSlide && this.wallSlideCounter > 0 && fallingSpeed < this.wallSlideVelocity && this.wallMaterialData?.canWallJump === true && this.allowWallJump) fallingSpeed = this.wallSlideVelocity;

    if (body.type !== BodyType.Static) {
      if (this.isGrounded && !this.isOnFlatGround && this.playerMovementSpeed !== 0) {
        const k = this.playerMovementSpeed + vdot(this.groundedOnBodySpeed, slopeDirection);
        body.velocity = { x: slopeDirection.x * k, y: slopeDirection.y * k };
      } else {
        body.velocity = { x: this.playerMovementSpeed + this.groundedOnBodySpeed.x, y: fallingSpeed + this.groundedOnBodySpeed.y };
      }
    }

    const bpd = this.bodyPushingDown;
    if (bpd && this.movementVectorDir !== 0 && sign(body.vx - bpd.vx) === sign(body.vx)) bpd.addForce({ x: this.movementVectorDir * this.movePushingDownBlockForce, y: 0 });
  }

  private shouldPerformJump() {
    return this.jumpDown && this.isGroundedCoyote && !this.hasJumpedSinceLastPress && this.jumpDisableCounter >= this.jumpDisableCounterMax && this.allowJump;
  }
  private shouldPerformWallJump() {
    return this.jumpDown && !this.hasJumpedSinceLastPress && !this.wallJumpCoyoteCounter.isFinished() && this.allowWallJump && this.lastWallMaterialData?.canWallJump === true;
  }
  private stillJumping() {
    return this.jumpDown && this.hasJumpedSinceLastPress && this.jumpHoldCounter <= this.jumpHoldFrames;
  }

  private handleJumping() {
    const body = this.body;
    if (this.shouldPerformJump() || this.shouldPerformWallJump()) {
      if (this.shouldPerformJump()) {
        body.velocity = { x: body.vx, y: this.jumpVelocity };
        for (const l of [...this.game.jumpListeners]) if (!l.go.destroyed) l.onPlayerJumped(this, false);
        this.sound('Jump');
      } else {
        this.sound('WallJump');
        this.playerMovementSpeed = this.wallJumpXVelocity * this.wallJumpDir;
        body.velocity = { x: this.playerMovementSpeed, y: this.wallJumpYVelocity };
        for (const l of [...this.game.jumpListeners]) if (!l.go.destroyed) l.onPlayerJumped(this, true);
        if (this.movementVectorDir === 0) this.characterDirection = this.wallJumpDir;
        this.isCollidingLeftWall = false;
        this.isCollidingRightWall = false;
        this.isCollidingLeftWithPlayer = false;
        this.isCollidingRightWithPlayer = false;
        this.postWallJumpCounter++;
      }
      this.isGrounded = false;
      this.isGroundedCoyote = false;
      this.jumpDisableCounter = 0;
      this.coyoteCounter = this.maxCoyoteCount + 1;
      this.hasJumpedSinceLastPress = true;
      this.jumpHoldCounter = 0;
      this.playerCollider.material = this.inAirMaterial;
    } else if (this.stillJumping()) {
      body.velocity = { x: body.vx, y: this.jumpVelocity };
      this.jumpHoldCounter++;
    }
    if (this.hasJumpedSinceLastPress && this.jumpReleasedThisFrame && body.vy > 0) body.velocity = { x: body.vx, y: body.vy * this.jumpReleaseMultiplier };
  }

  startDash(dir: V2, dontAdvanceDashCounter = false) {
    this.dashCounter = 1;
    this.dashAgainCounter = 1;
    this.postWallJumpCounter = 0;
    this.wallSlideCounter = 0;
    if (!dontAdvanceDashCounter) this.dashCountSinceLastGround++;
    if (dir.x === 0 && dir.y === 0) this.dashDirection = { x: this.characterDirection * this.dashVelocity, y: 0 };
    else {
      const n = vnorm(dir);
      this.dashDirection = { x: n.x * this.dashVelocity, y: n.y * this.dashVelocity };
    }
    for (const l of this.game.dashListeners) if (!l.go.destroyed) l.onPlayerDash(this);
  }

  private handleDashing() {
    if (this.dashPressedThisFrame && this.dashCountSinceLastGround < this.dashCount && this.allowDash && this.dashAgainCounter === 0) {
      this.startDash(this.onlyAllow45DegreeDash ? this.movementVector45 : this.movementVector);
      this.sound('Dash');
    }
    if (this.dashAgainCounter > 0) {
      this.dashAgainCounter++;
      if (this.dashAgainCounter > this.allowDashAgainFrames) this.dashAgainCounter = 0;
    }
    if (this.dashCounter > 0) {
      this.playerCollider.material = this.inAirMaterial;
      if (this.body.type !== BodyType.Static) this.body.velocity = { ...this.dashDirection };
      if (this.dashCounter < this.jumpDisableCounterMax) this.coyoteCounter = this.maxCoyoteCount + 1;
      this.dashCounter++;
      this.spawnDashTrail();
      if (this.dashCounter > this.dashFrames) this.finishDash();
    }
  }

  finishDash() {
    this.dashCounter = 0;
    if (this.dashDirection.x !== 0 || this.dashDirection.y > 0) {
      const n = vnorm(this.dashDirection);
      this.body.velocity = { x: n.x * this.postDashVelocity, y: n.y * this.postDashVelocity };
    }
    if (vlen(this.dashDirection) < vlen(this.body.velocity)) this.body.velocity = { ...this.dashDirection };
    this.hasDashBeenLimited = false;
    this.playerMovementSpeed = this.body.vx;
    this.dashFinishedThisFrame = true;
  }

  private spawnDashTrail() {
    const p = this.position;
    this.trail.push({ x: p.x, y: p.y, t: this.world.time, sprite: this.go.sprite?.sprite ?? null, scale: this.go.bodyScaleXY[0] });
    if (this.trail.length > 64) this.trail.splice(0, this.trail.length - 64);
  }

  private groundPlayer() {
    if (this.jumpDisableCounter >= this.jumpDisableCounterMax) {
      this.isGrounded = true;
      this.isGroundedCoyote = true;
      this.coyoteCounter = 0;
    }
    if (this.dashAgainCounter === 0) this.dashCountSinceLastGround = 0;
  }

  private isValidPlatformEffectorContact(c: SimCollider, normal: V2): boolean {
    const eff = c.effector;
    if (!eff || !eff.useOneWay) return true;
    return vangle(c.go.up, normal) <= eff.arc * 0.5;
  }

  private enumerateContacts() {
    this.previousDraggableRigidbody = this.draggableRigidbody;
    this.draggableRigidbody = null;
    const contacts: ContactPoint[] = this.body.getContacts().slice(0, 20);

    this.isGrounded = false;
    this.groundedOnBodySpeed = { x: 0, y: 0 };
    let groundContacts = 0;
    this.isCollidingLeftWall = false;
    this.isCollidingRightWall = false;
    this.isCollidingLeftWithPlayer = false;
    this.isCollidingRightWithPlayer = false;
    this.isPushingLeft = false;
    this.isPushingRight = false;
    this.isPushedDown = false;
    this.pushedDownImpulse = 0;
    this.bodyPushingDown = null;
    this.isPushedDownSlope = false;
    let slidingWallIsToLeft = false;
    let slidingWallIsToRight = false;
    let isTouchingSlidingSlope = false;
    this.groundSlideDirection = 0;
    let gn = { x: 0, y: 0 };
    const groundMaterials: (PhysMat | null)[] = [];
    const wallMaterials: (PhysMat | null)[] = [];
    const UP = { x: 0, y: 1 }, DOWN = { x: 0, y: -1 }, RIGHT = { x: 1, y: 0 }, LEFT = { x: -1, y: 0 };

    for (const contact of contacts) {
      if (!this.isValidPlatformEffectorContact(contact.collider, contact.normal)) continue;
      const n = contact.normal;
      const groundAngle = vangle(n, UP);
      const ceilingAngle = vangle(n, DOWN);
      const leftWallAngle = vangle(n, RIGHT);
      const rightWallAngle = vangle(n, LEFT);
      const rb = contact.rigidbody;
      const isPlayer = !!rb?.go.getBehaviour(PlayerController);
      const isOneWayPlatform = !!rb?.go.comps.has('PlatformEffector2D');

      if (rb && rb.go.comps.has('DashStopper') && (vlen(rb.velocity) > 0 || (rb.hasKV && vlen(rb.kvVelocity) > 0) || rb.type === BodyType.Static) && this.isDashing()) this.finishDash();

      if (ceilingAngle <= this.maxWallAngle) this.isPushedDown = true;
      if (ceilingAngle <= this.maxGroundAngle && rb && (rb.type === BodyType.Static || rb.type === BodyType.Kinematic)) this.isPushedDownSlope = true;
      if (ceilingAngle < this.maxGroundAngle && rb?.type === BodyType.Dynamic && contact.normalImpulse > this.pushedDownImpulse) {
        this.pushedDownImpulse = contact.normalImpulse;
        this.bodyPushingDown = rb;
      }

      if (groundAngle <= this.maxGroundAngle) {
        this.groundPlayer();
        gn = { x: gn.x + n.x, y: gn.y + n.y };
        groundMaterials.push(contact.collider.material);
        if (rb) {
          groundContacts++;
          if (rb.type === BodyType.Dynamic) {
            const rv = rb.velocity;
            this.groundedOnBodySpeed = { x: this.groundedOnBodySpeed.x + rv.x, y: this.groundedOnBodySpeed.y + rv.y };
          } else if (rb.hasKV) {
            this.groundedOnBodySpeed = { x: this.groundedOnBodySpeed.x + rb.kvVelocity.x, y: this.groundedOnBodySpeed.y + rb.kvVelocity.y };
          }
        }
      } else if (groundAngle > this.maxGroundAngle && groundAngle < 90 - this.maxWallAngle) {
        isTouchingSlidingSlope = true;
        this.groundSlideDirection = sign(n.x);
        if (this.groundSlideDirection > 0) slidingWallIsToLeft = true;
        if (this.groundSlideDirection < 0) slidingWallIsToRight = true;
      }

      if (isPlayer && this.isDashing()) {
        if (!this.allowDashLaunch) this.finishDash();
        if (this.limitDashLaunchMultiplier > 0 && !this.hasDashBeenLimited) {
          this.dashDirection = { x: this.dashDirection.x * this.limitDashLaunchMultiplier, y: this.dashDirection.y * this.limitDashLaunchMultiplier };
          this.hasDashBeenLimited = true;
        }
      }

      if (leftWallAngle <= this.maxWallAngle && !isOneWayPlatform) {
        this.isCollidingLeftWall = true;
        if (isPlayer) this.isCollidingLeftWithPlayer = true;
        wallMaterials.push(contact.collider.material);
      }
      if (rightWallAngle <= this.maxWallAngle && !isOneWayPlatform) {
        this.isCollidingRightWall = true;
        if (isPlayer) this.isCollidingRightWithPlayer = true;
        wallMaterials.push(contact.collider.material);
      }
      if (rb && rb.type === BodyType.Dynamic) {
        if (leftWallAngle <= this.maxPushAngle) this.isPushingLeft = true;
        if (rightWallAngle <= this.maxPushAngle) this.isPushingRight = true;
      }
    }

    if ((this.isCollidingLeftWall && slidingWallIsToRight) || (this.isCollidingRightWall && slidingWallIsToLeft) || (slidingWallIsToLeft && slidingWallIsToRight)) this.groundPlayer();

    if (this.isGrounded) this.characterContactState = ContactState.Grounded;
    else if ((this.isCollidingLeftWall && !this.isCollidingLeftWithPlayer) || (this.isCollidingRightWall && !this.isCollidingRightWithPlayer)) this.characterContactState = ContactState.Wall;
    else if (isTouchingSlidingSlope) this.characterContactState = ContactState.Sliding;
    else this.characterContactState = ContactState.None;
    if (this.characterContactState !== ContactState.Sliding) this.groundSlideDirection = 0;

    if (groundContacts > 0 && (this.groundedOnBodySpeed.x !== 0 || this.groundedOnBodySpeed.y !== 0)) {
      this.groundedOnBodySpeed = { x: this.groundedOnBodySpeed.x / groundContacts, y: this.groundedOnBodySpeed.y / groundContacts };
    }

    if (gn.x === 0 && gn.y === 0) {
      this.groundNormal = { x: 0, y: 1 };
      this.groundNormalAngle = 90;
      this.isOnFlatGround = false;
    } else {
      this.groundNormal = vnorm(gn);
      this.groundNormalAngle = (Math.atan2(this.groundNormal.y, this.groundNormal.x) * 180) / Math.PI;
      if (this.groundNormalAngle < 90.1 && this.groundNormalAngle > 89.9) {
        this.groundNormalAngle = 90;
        this.groundNormal = { x: 0, y: 1 };
        this.isOnFlatGround = true;
      } else this.isOnFlatGround = false;
    }
    if (!this.isGrounded) this.isOnFlatGround = false;

    // grab box
    const b = this.playerCollider.bounds();
    if (b) {
      const p = this.body.position;
      const off = this.colliderOffsetV();
      const [sx, sy] = this.go.bodyScaleXY;
      const center = { x: p.x + off.x * sx + this.grabDistance * this.characterDirection, y: p.y + off.y * sy };
      const size = { x: (b[2] - b[0]) * 1, y: (b[3] - b[1]) * 0.8 };
      for (const hit of this.world.overlapBox(center, size, 0)) {
        const rb = hit.attachedRigidbody;
        if (rb === this.body || rb?.type !== BodyType.Dynamic) continue;
        if (!this.draggableRigidbody || this.draggableRigidbody.position.y > rb.position.y) this.draggableRigidbody = rb;
      }
    }
    if (!this.draggableRigidbody && this.isDragging && this.input.interactDown && this.previousDraggableRigidbody) this.draggableRigidbody = this.previousDraggableRigidbody;

    this.groundMaterialData = getMaterialData(groundMaterials);
    this.wallMaterialData = getMaterialData(wallMaterials);
    if (this.wallMaterialData !== MATERIAL_DATA.air) this.lastWallMaterialData = this.wallMaterialData;
  }

  // ---------- squishable ----------

  private squishableFixedUpdate() {
    if (!this.body || this.playerState !== PlayerState.Playing) return;
    const all = this.body.getContacts().slice(0, 16);
    if (all.length < 2) return;
    const effOf = (cp: ContactPoint) => (cp.collider.usedByEffector && cp.collider.effector ? cp.collider : cp.otherCollider.usedByEffector && cp.otherCollider.effector ? cp.otherCollider : null);
    const blocking = all.filter((cp) => {
      if (cp.collider.isTrigger || cp.otherCollider.isTrigger) return false;
      const pe = effOf(cp);
      if (!pe || !pe.effector!.useOneWay) return true;
      return vangle(pe.go.up, cp.normal) <= pe.effector!.arc * 0.5;
    });
    let overlap = false;
    if (blocking.length >= 2) {
      let best = 1;
      let a: ContactPoint | null = null, b: ContactPoint | null = null;
      for (let i = 0; i < blocking.length; i++) {
        for (let j = i + 1; j < blocking.length; j++) {
          const d = vdot(blocking[i].normal, blocking[j].normal);
          if (d < best) {
            best = d;
            a = blocking[i];
            b = blocking[j];
          }
        }
      }
      if (best < 0 && a && b) {
        if ((-a.separation > this.penetrationThreshold && !effOf(a)) || (-b.separation > this.penetrationThreshold && !effOf(b))) overlap = true;
      }
    }
    if (overlap) {
      if (this.squishCounter.isNotRunning()) this.squishCounter.start();
      else if (this.squishCounter.countCheckFinishReset()) {
        this.squishCounter.reset();
        if (this.willDie({ primary: PrimaryWaysToDie.Squish })) this.sound('squish');
        this.beginDeath({ primary: PrimaryWaysToDie.Squish });
      }
    } else this.squishCounter.reset();
  }

  // ---------- death / respawn ----------

  beginDeath(d: Deathod) {
    this.dieLogic(d);
  }

  willDie(d: Deathod) {
    return this.dieLogic(d, true);
  }

  private dieLogic(d: Deathod, testOnly = false): boolean {
    if (this.isUsingDebugControl || this.invulnerableToDeath) return false;
    if (this.isKindaDyingAlready()) return false;
    if (this.powerup && !this.powerup.canDie(d)) return false;
    if (testOnly) return true;
    this.sound('PlayerDeath');
    if (this.game.allowDeathCheckpoint()) this.game.startCheckpointLoadCounter();
    const p = this.position;
    this.game.emit({ kind: 'death', player: this.index, x: p.x, y: p.y, color: this.color });
    const deadGuid = this.go.comps.get('PlayerController')?.deadPlayerPrefab?.guid;
    const dead = deadGuid ? this.game.instantiate(String(deadGuid), p, this.go.rotation) : null;
    const ex = dead?.getBehaviour(DeadQuarterExploder);
    if (ex && this.go.sprite) ex.sourceSprite = { ...this.go.sprite, color: [...this.go.sprite.color] as RGBA };
    if (dead && this.powerup?.type === PowerupType.BigDude) {
      const [sx, sy] = this.go.bodyScaleXY;
      for (const g of dead.descendants()) if (g.body) g.setBodyScale(sx, sy);
    }
    this.playerCollider.enabled = false;
    this.body.type = BodyType.Static;
    this.playerState = PlayerState.Dying;
    this.controlPanelInRange = null;
    this.doorInRange = null;
    if (this.go.sprite) this.go.sprite.enabled = false;
    this.deathCount++;
    this.totalDeaths++;
    for (const fn of this.game.onPlayerDied) fn(this);
    return true;
  }

  /** PlayerController.Respawn(). keepProgress leaves coins, score, and the current powerup (king of the hill). */
  respawnFresh(pos: V2, keepProgress = false) {
    if (!keepProgress) this.powerup?.removePowerup();
    this.playerState = PlayerState.Playing;
    this.isGrounded = false;
    this.groundedOnBodySpeed = { x: 0, y: 0 };
    this.isGroundedCoyote = false;
    this.isCollidingLeftWall = this.isCollidingRightWall = this.isCollidingLeftWithPlayer = this.isCollidingRightWithPlayer = false;
    this.isPushingLeft = this.isPushingRight = false;
    this.playerMovementSpeed = 0;
    this.coyoteCounter = 0;
    this.jumpDisableCounter = 0;
    this.jumpHoldCounter = 0;
    this.wallSlideCounter = 0;
    this.dashCounter = 0;
    this.dashAgainCounter = 0;
    this.dashCountSinceLastGround = 0;
    this.jumpDown = false;
    this.jumpReleasedThisFrame = false;
    this.hasJumpedSinceLastPress = false;
    this.dashPressedThisFrame = false;
    this.characterDirection = 1;
    this.isCrouching = false;
    this.isPressingCrouch = false;
    this.characterDisplayState = DisplayState.StandingRight;
    this.characterContactState = ContactState.None;
    this.groundSlideDirection = 0;
    this.isDragging = false;
    this.draggableRigidbody = null;
    this.controlPanelInRange = null;
    this.doorInRange = null;
    this.isUsingControlPanel = false;
    this.isUsingDebugControl = false;
    this.isPushedDown = false;
    this.pushedDownImpulse = 0;
    this.bodyPushingDown = null;
    this.isPushedDownSlope = false;
    this.isExternallyControlled = false;
    this.levelFinishedOnFrame = 0;
    if (!keepProgress) this.coinCount = this.gemCount = this.shardCount = this.score = 0;
    this.body.type = BodyType.Dynamic;
    this.body.position = pos;
    this.body.velocity = { x: 0, y: 0 };
    this.body.angularVelocity = 0;
    this.body.rotation = 0;
    this.body.resetKV();
    if (this.go.sprite) {
      this.go.sprite.enabled = true;
      this.go.sprite.color = [...this.color] as RGBA;
      this.go.sprite.order = 0;
      if (this.go.sprite.sortingLayer === PlayerController.FLY_LAYER) this.go.sprite.sortingLayer = this.savedSortLayer ?? 'Player';
    }
    this.savedSortLayer = null;
    this.playerCollider.enabled = true;
    this.trail = [];
    this.updateSprite();
    if (this.totalDeaths > 0) this.sound('respawn');
  }

  afterCheckpointRestore() {
    if (this.go.sprite) this.go.sprite.color = [...this.color] as RGBA;
    this.trail = [];
  }

  // ---------- powerups / doors / control panels ----------

  givePowerup(t: PowerupType) {
    this.powerup?.removePowerup();
    this.powerup = constructPowerup(t, this);
    this.sound('Powerup');
    this.score += 500;
  }

  performPowerupAction(a: 'pressed' | 'released' | 'remove') {
    if (a === 'pressed') this.powerup?.powerupPressed();
    else if (a === 'released') this.powerup?.powerupReleased();
    else if (this.powerup) {
      this.powerup.removePowerup();
      this.sound('powerupEnd');
    }
  }

  private handleInteractables() {
    if (this.input.interactPressed) {
      if (this.doorInRange) {
        if (this.playerState === PlayerState.InDoor) this.changeDoorState(false);
        else if (this.playerState === PlayerState.Playing) this.changeDoorState(true);
      }
      if (this.isUsingControlPanel) this.endControlPanel();
      else if (this.controlPanelInRange && !this.isDashing() && this.isGrounded) this.beginControlPanel();
    }
  }

  private changeDoorState(isInDoor: boolean) {
    const door = this.doorInRange!;
    if (isInDoor) {
      this.game.onEnterDoor?.(this);
      this.playerState = PlayerState.InDoor;
      this.playerCollider.enabled = false;
      door.onPlayerEntered(this);
      this.levelFinishedOnFrame = this.game.frame;
      if (this.go.sprite) this.go.sprite.order = -1;
    } else {
      this.playerState = PlayerState.Playing;
      const b = door.doorBounds();
      const [, sy] = this.go.bodyScaleXY;
      this.body.position = { x: (b[0] + b[2]) / 2, y: b[1] + Math.abs(sy) / 2 };
      this.body.velocity = { x: 0, y: 0 };
      door.onPlayerLeft(this);
      this.levelFinishedOnFrame = 0;
      this.playerCollider.enabled = true;
      if (this.go.sprite) this.go.sprite.order = 0;
    }
  }

  beginControlPanel() {
    if (this.controlPanelInRange) {
      this.isUsingControlPanel = true;
      this.controlPanelInRange.startControl(this);
      this.sound('controlPanel');
    }
  }

  endControlPanel() {
    if (this.isUsingControlPanel) this.sound('controlPanelOff');
    this.isUsingControlPanel = false;
    this.controlPanelInRange?.endControl();
  }

  sound(name: string) {
    const p = this.position;
    this.game.emit({ kind: 'sound', name, x: p.x, y: p.y });
  }
}

export function isPlayerGo(go: import('./world.ts').SimGo | null | undefined): PlayerController | null {
  return go ? go.getBehaviour(PlayerController) : null;
}

function snValue(field: unknown): unknown {
  if (!field || typeof field !== 'object') return undefined;
  const f = field as { hasValue?: unknown; v?: unknown };
  if (f.hasValue === false || String(f.hasValue ?? '0') === '0') return undefined;
  return f.v;
}

/**
 * PlayerController.ApplyOverrides. Only fields with hasValue set replace the prefab.
 * allowDashLaunching / dashLaunchMultiplier are the inspector names for allowDashLaunch / limitDashLaunchMultiplier.
 */
export function applyPlayerOverrides(p: PlayerController, data: Record<string, unknown> | null | undefined) {
  if (!data || String(data.m_Enabled ?? '1') === '0') return;
  const flag = (key: string, set: (v: boolean) => void) => {
    const v = snValue(data[key]);
    if (v !== undefined) set(bool(v, false));
  };
  const val = (key: string, set: (v: number) => void) => {
    const v = snValue(data[key]);
    if (v === undefined) return;
    const n = Number(v);
    if (Number.isFinite(n)) set(n);
  };
  flag('allowJump', (v) => { p.allowJump = v; });
  flag('allowDash', (v) => { p.allowDash = v; });
  flag('allowDashLaunching', (v) => { p.allowDashLaunch = v; });
  val('dashLaunchMultiplier', (v) => { p.limitDashLaunchMultiplier = v; });
  val('dashCount', (v) => { p.dashCount = v; });
  flag('allowWallJump', (v) => { p.allowWallJump = v; });
  flag('allowWallSlide', (v) => { p.allowWallSlide = v; });
  flag('allowControl', (v) => { p.allowControl = v; });
  flag('killOnPressRestart', (v) => { p.killOnPressRestart = v; });
  val('maxMovementSpeed', (v) => { p.maxMovementSpeed = v; });
  val('jumpVelocity', (v) => { p.jumpVelocity = v; });
  val('wallJumpXVelocity', (v) => { p.wallJumpXVelocity = v; });
  val('wallJumpYVelocity', (v) => { p.wallJumpYVelocity = v; });
  val('dashVelocity', (v) => { p.dashVelocity = v; });
  val('postDashVelocity', (v) => { p.postDashVelocity = v; });
  val('wallSlideVelocity', (v) => { p.wallSlideVelocity = v; });
  val('maxCoyoteCount', (v) => { p.maxCoyoteCount = v; });
  val('dashFrames', (v) => { p.dashFrames = v; });
  val('allowDashAgainFrames', (v) => { p.allowDashAgainFrames = v; });
  val('jumpHoldFrames', (v) => { p.jumpHoldFrames = v; });
  val('wallSlideCounterMax', (v) => { p.wallSlideCounterMax = v; });
  val('postWallJumpCounterMax', (v) => { p.postWallJumpCounterMax = v; });
  flag('onlyAllow45DegreeDash', (v) => { p.onlyAllow45DegreeDash = v; });
  val('jumpReleaseMultiplier', (v) => { p.jumpReleaseMultiplier = v; });
  val('maxGroundAngle', (v) => { p.maxGroundAngle = v; });
  val('maxWallAngle', (v) => { p.maxWallAngle = v; });
}

/** PlayerControllerColorHandler.ColorToHSL / HSLToColor. Hue is degrees. */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  if (delta === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let h: number;
  if (max === r) h = (g - b) / delta + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  return { h: h * 60, s, l };
}

function hueToRgb(p: number, q: number, t: number): number {
  if (t < 0) t += 360;
  if (t > 360) t -= 360;
  if (t < 60) return p + (q - p) * t / 60;
  if (t < 180) return q;
  if (t < 240) return p + (q - p) * (240 - t) / 60;
  return p;
}

function hslToRgb(h: number, s: number, l: number): RGBA {
  if (s === 0) return [l, l, l, 1];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hueToRgb(p, q, h + 120), hueToRgb(p, q, h), hueToRgb(p, q, h - 120), 1];
}
